import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Navigate, Route, Routes } from "react-router-dom";
import MainLayout from "./components/Layout/MainLayout";
import ThemeToggle from "./components/ThemeToggle/ThemeToggle";
import { ROUTES } from "./constants";
import { useAuth } from "./hooks/useAuth";
import { useRbac } from "./hooks/RbacContext.js";
import Login from "./views/Login/Login";
import LandingPage from "./views/LandingScreen/LandingPage";
import DbDiagramView from "./views/DbDiagramView";
import { AskFlashDice } from "./views/AskFlashDice/AskFlashDice.js";
import { Collaboration } from "./views/Collaboration/Collaboration.js";
import SavedQuery from "./views/SavedQuery/SavedQuery";
import FileUpload from "./views/FileUpload/FileUpload";
import AllRoles from "./views/Roles/AllRoles.js";
import Users from "./views/UserAccessManagement/Users.js";
import ReviewSql from "./views/ReviewSql/ReviewSql.js";
import BusinessDefinition from "./views/BusinessDefinition/BusinessDefinition.js";
import Unauthorized from "./components/Unauthorized/Unauthorized.js";

const PrivateRoute = ({ children }) => {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) return <Navigate to={ROUTES.LOGIN} replace />;
  return children;
};

const PublicLayout = ({ children }) => (
  <Box sx={{ height: "100vh", position: "relative" }}>
    <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }}>
      <ThemeToggle />
    </Box>
    {children}
  </Box>
);

const PrivateLayout = ({ children }) => (
  <MainLayout>
    {children}
    {/* <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1200 }}>
      <ThemeToggle />
    </Box> */}
  </MainLayout>
);

const AppRoutes = () => {
  const { isLoggedIn } = useAuth();

  const [hasSelectedDb, setHasSelectedDb] = useState(
    Boolean(
      typeof window !== "undefined" &&
        localStorage.getItem("selected_user_db_id")
    )
  );

  const {
    ProtectedRoute,
    loading: rbacLoading,
    permissions,
    hasModuleAccess,
  } = useRbac();

  // Keep selected DB state in sync
  useEffect(() => {
    const checkDatabaseSelection = () => {
      const hasDb = Boolean(localStorage.getItem("selected_user_db_id"));
      setHasSelectedDb(hasDb);
    };

    checkDatabaseSelection();

    const handleDatabaseChange = () => checkDatabaseSelection();

    window.addEventListener(
      "selected_user_db_id_changed",
      handleDatabaseChange
    );
    window.addEventListener("storage", handleDatabaseChange);

    return () => {
      window.removeEventListener(
        "selected_user_db_id_changed",
        handleDatabaseChange
      );
      window.removeEventListener("storage", handleDatabaseChange);
    };
  }, []);

  // Show a lightweight splash while RBAC perms load (only after login)
  if (isLoggedIn && rbacLoading && !permissions) {
    return <Box sx={{ p: 3 }}>Loading permissions…</Box>;
  }
  const roleAwareDefault = () => {
    // console.log("permissions", permissions);

    if (permissions?.globalRole?.role_id === 13) {
      return ROUTES.DASHBOARD;
    }
    return ROUTES.DASHBOARD;
  };

  return (
    <Routes>
      {/* LOGIN: if already logged in + perms loaded, push user to role-aware default */}
      <Route
        path={ROUTES.LOGIN}
        element={
          isLoggedIn && permissions?.globalRole?.role_id ? (
            <Navigate to={roleAwareDefault()} replace />
          ) : (
            <PublicLayout>
              <Login />
            </PublicLayout>
          )
        }
      />

      {/* DASHBOARD (requires auth). Show ImportData when no DB selected. */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <PrivateRoute>
            <PrivateLayout>
              {/* Guard by module if desired: module="dashboard" */}
              <ProtectedRoute module="dashboard">
                <LandingPage />
                {/* {hasSelectedDb ? <LandingPage /> : <ImportData />} */}
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Root → role-aware redirect */}
      <Route
        path={ROUTES.HOME}
        element={
          <PrivateRoute>
            <Navigate to={roleAwareDefault()} replace />
          </PrivateRoute>
        }
      />

      {/* Roles Management */}
      <Route
        path={ROUTES.ROLES}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="role">
                <AllRoles />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Users Management */}
      <Route
        path={ROUTES.USERMANAGEMENT}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="userManagement">
                <Users />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Review SQL */}
      <Route
        path={ROUTES.REVIEW_SQL}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="reviewSql">
                <ReviewSql />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Business Definition */}
      <Route
        path={ROUTES.BUSINESS_DEFINITION}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="businessDefinition">
                <BusinessDefinition />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* DB Diagram */}
      <Route
        path={ROUTES.DB_DIAGRAM}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="dashboard">
                <DbDiagramView />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Ask FlashDice - with session (load conversation by session_id) */}
      <Route
        path={`${ROUTES.ASK_FLASHDICE}/:sessionId`}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="askFlashdice">
                <AskFlashDice />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />
      {/* Ask FlashDice - new conversation (no session in URL) */}
      <Route
        path={ROUTES.ASK_FLASHDICE}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="askFlashdice">
                <AskFlashDice />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Saved Queries */}
      <Route
        path={ROUTES.SAVED_LIST}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="savedQueries">
                <SavedQuery />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Collaboration */}
      <Route
        path={ROUTES.COLLABORATION}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="collaboration">
                <Collaboration />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      <Route
        path={ROUTES.UNAUTHORIZED}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <Unauthorized />
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* File upload */}
      <Route
        path={ROUTES.FILE_UPLOAD}
        element={
          <PrivateRoute>
            <PrivateLayout>
              <ProtectedRoute module="fileUpload">
                <FileUpload />
              </ProtectedRoute>
            </PrivateLayout>
          </PrivateRoute>
        }
      />

      {/* Unauthorized fallback (use your ROUTES constant if present) */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Catch-all: if authed go to default landing, else to login */}
      <Route
        path="*"
        element={
          isLoggedIn ? (
            <Navigate to={roleAwareDefault()} replace />
          ) : (
            <Navigate to={ROUTES.LOGIN} replace />
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
